import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ConditionBuilder } from '../condition-builder/ConditionBuilder';
import { useKeyboardSave } from './use-keyboard-save';
import type { SingleValueRuleElement, Condition, RegoValue } from '@/types/policy-editor';

interface DecisionFormProps {
    element: SingleValueRuleElement | null;
    onSave: (element: SingleValueRuleElement) => void;
    onCancel: () => void;
}

export function DecisionForm({ element, onSave, onCancel }: DecisionFormProps) {
    const [description, setDescription] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [allow, setAllow] = useState(true);
    const [reason, setReason] = useState('');
    const [code, setCode] = useState('');
    const [obligationsText, setObligationsText] = useState('{}');
    const [metadataText, setMetadataText] = useState('{}');
    const [conditions, setConditions] = useState<Condition[]>([]);

    useEffect(() => {
        if (element) {
            setDescription(element.description ?? '');
            setIsDefault(element.isDefault ?? false);
            setConditions(element.conditions);
            const dec = extractDecisionFields(element.value);
            setAllow(dec.allow); setReason(dec.reason); setCode(dec.code);
            setObligationsText(dec.obligations); setMetadataText(dec.metadata);
        } else {
            setDescription(''); setIsDefault(false); setAllow(true); setReason('');
            setCode('ALLOW_OK'); setObligationsText('{}'); setMetadataText('{}'); setConditions([]);
        }
    }, [element]);

    const handleSave = useCallback(() => {
        const value = buildDecisionValue({ allow, reason, code, obligations: obligationsText, metadata: metadataText });
        onSave({
            id: element?.id ?? `el-${Date.now()}`,
            elementType: 'singleValueRule',
            name: 'decision',
            description: description || undefined,
            value,
            conditions: isDefault ? [] : conditions,
            isDefault,
        });
    }, [element, description, isDefault, allow, reason, code, obligationsText, metadataText, conditions, onSave]);

    useKeyboardSave(handleSave);

    return (
        <div className="flex h-full flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
                <div className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                    The decision output is the primary result of this policy. It defines
                    whether an action is allowed, denied, or requires further obligations.
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="dec-desc">Description</Label>
                    <Input id="dec-desc" placeholder="When does this decision apply?" value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>

                <div className="flex items-center gap-2">
                    <Switch id="dec-default" checked={isDefault} onCheckedChange={setIsDefault} />
                    <Label htmlFor="dec-default" className="text-sm">Default decision (fallback when no other decision matches)</Label>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                        <Switch id="dec-allow" checked={allow} onCheckedChange={setAllow} />
                        <Label htmlFor="dec-allow" className="text-sm">{allow ? 'Allow' : 'Deny'}</Label>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="dec-code">Decision code</Label>
                        <Input id="dec-code" placeholder="e.g. ALLOW_OK, DENY_MISSING_SKILLS" value={code} onChange={(e) => setCode(e.target.value)} />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="dec-reason">Reason</Label>
                    <Input id="dec-reason" placeholder="Human-readable reason for this decision" value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="dec-obligations">Obligations (JSON)</Label>
                    <Textarea id="dec-obligations" className="min-h-[80px] font-mono text-xs" placeholder='{"profile": "balanced"}' value={obligationsText} onChange={(e) => setObligationsText(e.target.value)} />
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="dec-metadata">Metadata (JSON)</Label>
                    <Textarea id="dec-metadata" className="min-h-[80px] font-mono text-xs" placeholder='{"tier": "enterprise"}' value={metadataText} onChange={(e) => setMetadataText(e.target.value)} />
                </div>

                {!isDefault && <ConditionBuilder conditions={conditions} onChange={setConditions} />}
            </div>

            <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-[10px] text-slate-400">Ctrl+Enter to save</span>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
                    <Button size="sm" onClick={handleSave}>Save</Button>
                </div>
            </div>
        </div>
    );
}

interface DecisionFields { allow: boolean; reason: string; code: string; obligations: string; metadata: string; }

function extractDecisionFields(val: RegoValue): DecisionFields {
    const defaults: DecisionFields = { allow: true, reason: '', code: 'ALLOW_OK', obligations: '{}', metadata: '{}' };
    if (val.kind === 'object') {
        for (const entry of val.entries) {
            if (entry.key === 'allow' && entry.value.kind === 'scalar') defaults.allow = entry.value.value === true;
            else if (entry.key === 'reason' && entry.value.kind === 'scalar') defaults.reason = String(entry.value.value ?? '');
            else if (entry.key === 'code' && entry.value.kind === 'scalar') defaults.code = String(entry.value.value ?? '');
            else if (entry.key === 'obligations') defaults.obligations = stringifyRegoValue(entry.value);
            else if (entry.key === 'metadata') defaults.metadata = stringifyRegoValue(entry.value);
        }
    } else if (val.kind === 'raw') {
        try {
            const parsed = JSON.parse(val.text);
            if (parsed.allow !== undefined) defaults.allow = parsed.allow;
            if (parsed.reason) defaults.reason = parsed.reason;
            if (parsed.code) defaults.code = parsed.code;
            if (parsed.obligations) defaults.obligations = JSON.stringify(parsed.obligations, null, 2);
            if (parsed.metadata) defaults.metadata = JSON.stringify(parsed.metadata, null, 2);
        } catch { /* use defaults */ }
    }
    return defaults;
}

function stringifyRegoValue(val: RegoValue): string {
    if (val.kind === 'scalar') return JSON.stringify(val.value);
    if (val.kind === 'raw') return val.text;
    if (val.kind === 'object') {
        const obj: Record<string, unknown> = {};
        for (const e of val.entries) obj[e.key] = stringifyRegoValue(e.value);
        return JSON.stringify(obj, null, 2);
    }
    return '{}';
}

function buildDecisionValue(fields: DecisionFields): RegoValue {
    return {
        kind: 'object',
        entries: [
            { key: 'allow', value: { kind: 'scalar', value: fields.allow } },
            { key: 'reason', value: { kind: 'scalar', value: fields.reason } },
            { key: 'code', value: { kind: 'scalar', value: fields.code } },
            { key: 'obligations', value: parseJsonOrRaw(fields.obligations) },
            { key: 'metadata', value: parseJsonOrRaw(fields.metadata) },
        ],
    };
}

function parseJsonOrRaw(text: string): RegoValue {
    try { return jsonToRegoValue(JSON.parse(text)); }
    catch { return { kind: 'raw', text }; }
}

function jsonToRegoValue(val: unknown): RegoValue {
    if (val === null) return { kind: 'scalar', value: null };
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return { kind: 'scalar', value: val };
    if (Array.isArray(val)) return { kind: 'array', items: val.map(jsonToRegoValue) };
    if (typeof val === 'object') {
        return { kind: 'object', entries: Object.entries(val as Record<string, unknown>).map(([k, v]) => ({ key: k, value: jsonToRegoValue(v) })) };
    }
    return { kind: 'scalar', value: null };
}
